import React, { useState } from 'react';
import axios from 'axios';
import logo from '../img/logo.png';
import loginIllustration from '../img/login_illustration.png';
import { useNavigate } from 'react-router-dom';
import {
    TextField,
    Snackbar,
    Alert as MuiAlert,
    Button,
    Typography,
    Box,
    Link,
    InputAdornment,
    IconButton
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Email,
    Lock,
    ArrowForward
} from '@mui/icons-material';
import flaskServer from '../config/flaskserver';
import '../layout/Login.css';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Login = ({ setToken, handleLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [showConfirmDisconnect, setShowConfirmDisconnect] = useState(false);
    const [loginFailed, setLoginFailed] = useState(false);

    const navigate = useNavigate();
    const [loginSnackbarOpen, setLoginSnackbarOpen] = useState(false);
    const [loginSnackbarMessage, setLoginSnackbarMessage] = useState('');

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event) => event.preventDefault();

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${flaskServer.flaskServerIP}/login`, { email, password });

            if (response.data.token) {
                const token = response.data.token;
                localStorage.setItem('token', token);
                setToken(token);
                handleLogin();
                navigate('/');
            } else if (response.data.confirm_disconnect) {
                setShowConfirmDisconnect(true);
                setSnackbarMessage('Número máximo de sessões simultâneas atingido. Deseja desconectar uma sessão ativa para continuar?');
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
            } else if (response.data.data_expirada) {
                setSnackbarMessage('A conta expirou. Por favor, entre em contato com o suporte.');
                setSnackbarSeverity('warning');
                setSnackbarOpen(true);
            } else {
                setSnackbarMessage('Erro ao fazer login. Por favor, tente novamente.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                setLoginFailed(true);
            }
        } catch (error) {
            setSnackbarMessage('Usuário ou senha inválidos. Por favor, tente novamente.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setLoginFailed(true);
        }
    };

    const handleForgotPassword = async () => {
        try {
            const response = await axios.post(`${flaskServer.flaskServerIP}/forgot-password`, { email });
            if (response.data.message === 'E-mail de recuperação de senha enviado com sucesso') {
                setSnackbarMessage(response.data.message);
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
            } else {
                setSnackbarMessage('Erro ao solicitar recuperação de senha. Por favor, tente novamente.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        } catch (error) {
            setSnackbarMessage('Erro ao solicitar recuperação de senha. Por favor, tente novamente.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleConfirmDisconnect = async () => {
        try {
            const response = await axios.post(`${flaskServer.flaskServerIP}/confirm-disconnect`, { email, password });
            if (response.data.token) {
                const token = response.data.token;
                localStorage.setItem('token', token);
                setToken(token);
                handleLogin();
                navigate('/');
            } else {
                setSnackbarMessage('Erro ao continuar login. Por favor, tente novamente.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        } catch (error) {
            setSnackbarMessage('Erro ao continuar login. Por favor, tente novamente.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
        setSnackbarOpen(false);
        setTimeout(() => {
            setLoginSnackbarMessage('Efetue o Login novamente');
            setLoginSnackbarOpen(true);
        }, 0);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const handleCloseLoginSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setLoginSnackbarOpen(false);
    };

    return (
        <Box className="login-page-container">
            <Box className="login-left-pane">
                <Box className="login-card">
                    <Box className="login-header-section">
                        <Box className="login-logo-row">
                            <Box className="login-logo-wrapper">
                                <img src={logo} alt="Logo" className="login-logo" />
                            </Box>
                            <Typography className="login-system-name">
                                SACCI
                            </Typography>
                        </Box>
                        <Typography className="login-system-label">
                            Sistema de Auditoria e Conciliação Contábil Inteligente
                        </Typography>
                    </Box>

                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                        <Box className="sach-input-group">
                            <Typography className="sach-input-label">Usuário</Typography>
                            <TextField
                                className="sach-input"
                                required
                                fullWidth
                                id="email"
                                name="email"
                                autoComplete="email"
                                autoFocus
                                placeholder="Digite seu usuário"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Box className="sach-input-group">
                            <Typography className="sach-input-label">Senha</Typography>
                            <TextField
                                className="sach-input"
                                required
                                fullWidth
                                name="password"
                                placeholder="Digite sua senha"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForward />}
                            sx={{
                                py: 1.8,
                                mt: 1,
                                fontSize: '1.1rem',
                                fontWeight: 700,
                                textTransform: 'none',
                                borderRadius: 2.5,
                                backgroundColor: '#6366f1',
                                boxShadow: '0 8px 16px -4px rgba(99, 102, 241, 0.4)',
                                '&:hover': {
                                    backgroundColor: '#4f46e5',
                                    boxShadow: '0 12px 20px -4px rgba(99, 102, 241, 0.4)',
                                }
                            }}
                        >
                            Entrar
                        </Button>

                        <Box sx={{ mt: 8, textAlign: 'center' }}>
                            <Typography variant="caption" color="textSecondary" sx={{ opacity: 0.6 }}>
                                © 2025 SACCI. Todos os direitos reservados.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box className="login-right-pane">
                <img src={loginIllustration} alt="" className="login-illustration-bg" />
                <Box className="login-branding">
                    <Typography variant="h1">
                        Auditoria e Conciliação Inteligente
                    </Typography>
                    <Typography variant="body1">
                        Conciliação contábil e auditoria de contas com precisão, eficiência e confiabilidade. 
                    </Typography>
                </Box>
            </Box>

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {showConfirmDisconnect ? (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Número máximo de sessões simultâneas atingido. Deseja desconectar uma sessão ativa para continuar?
                            </Typography>
                            <Button color="inherit" size="small" onClick={handleConfirmDisconnect}>
                                Confirmar desconexão
                            </Button>
                        </Box>
                    ) : (
                        snackbarMessage
                    )}
                </Alert>
            </Snackbar>

            <Snackbar open={loginSnackbarOpen} autoHideDuration={6000} onClose={handleCloseLoginSnackbar}>
                <Alert onClose={handleCloseLoginSnackbar} severity="info" sx={{ width: '100%' }}>
                    {loginSnackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Login;

